import { createContext, useContext } from 'react';

/**
 * Provides global app-level state — backend selection, model, call state,
 * theme, privacy toggle — to any component without prop drilling.
 *
 * Value shape (additive — older keys remain):
 *   { selectedBackend, setSelectedBackend,
 *     selectedModel,   setSelectedModel,
 *     callState,       setCallState,
 *     isCallStatusEnabled, setCallStatusEnabled,
 *     hideBalances,    setHideBalances }
 */
export const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

// Drops the digits from a CAD-formatted string and shows masked dots.
// Used by every screen that renders a balance so flipping the eye in the
// home top bar hides them globally. Accepts already-formatted strings or
// raw numbers — returns the formatted string unchanged when hide is off.
// Note: the `$` prefix is intentionally omitted from the masked output
// per design feedback — a dollar sign in front of the dots reads weird.
export function maskAmount(formatted, hide) {
  if (!hide) return formatted;
  if (typeof formatted === 'number') return '••••';
  if (typeof formatted !== 'string') return formatted;
  const s = formatted.trim();
  // Preserve a leading sign (- or –) so debt indicators don't collapse
  // to the same glyph as an asset.
  const signMatch = s.match(/^([-–])\s*\$?/);
  if (signMatch) return `${signMatch[1]}••••`;
  return '••••';
}
